import logger from '@/lib/logger';
import type { Triggers, ImpedanceExec, DevMessage, ImpedanceResult } from '@/lib/protobuf';
import { ElectrodeDetect, PowerState, CHAN } from '@/lib/protobuf';
import type { MeasurementResult } from '@/types/device';
import type { ConnectResult, MeasurementOptions } from './types';
import type { IDeviceService, TransferIncident } from './IDeviceService';
import { useDeviceStore } from '@/store';

/**
 * MockDeviceService - Simulates device communication for testing without hardware
 * 
 * Provides realistic delays and responses to enable full wizard testing in mock mode.
 */
export class MockDeviceService implements IDeviceService {
  private isConnected = false;
  private isMeasuring = false;
  private measurementPhase: 'baseline' | 'specimen' = 'baseline';

  /**
   * Mock always returns true (no real WebUSB needed)
   */
  isWebUSBSupported(): boolean {
    return true;
  }

  /**
   * Mock always returns true (no real Bluetooth adapter needed)
   */
  isBleSupported(): boolean {
    return true;
  }

  /**
   * Set measurement phase to generate appropriate data
   */
  setMeasurementPhase(phase: 'baseline' | 'specimen'): void {
    this.measurementPhase = phase;
    logger.debug(`[MockDeviceService] Measurement phase set to: ${phase}`);
  }

  /**
   * Mock: no watchdog messages, returns a no-op unsubscribe
   */
  onUnsolicitedMessage(_handler: (msg: import('@/lib/protobuf').DevMessage, _raw: Uint8Array) => void): () => void {
    return () => {};
  }

  /**
   * Mock: no wire traffic to report, returns a no-op unsubscribe
   */
  onSentMessage(
    _handler: (raw: Uint8Array, message: import('@/lib/protobuf').AppMessage) => void
  ): () => void {
    return () => {};
  }

  /**
   * Mock: no wire traffic to report, returns a no-op unsubscribe
   */
  onReceivedMessage(
    _handler: (
      raw: Uint8Array,
      msg: import('@/lib/protobuf').DevMessage,
      crcValid: boolean
    ) => void
  ): () => void {
    return () => {};
  }

  /**
   * Mock: no transferIn failures to report, returns a no-op unsubscribe
   */
  onTransferIncident(_handler: (incident: TransferIncident) => void): () => void {
    return () => {};
  }

  /**
   * Simulate device connection with delay
   */
  async connect(): Promise<ConnectResult> {
    // Simulate connection delay
    await this.delay(500);

    // Reset measurement flag on fresh connection
    if (this.isMeasuring) {
      logger.warn('[MockDeviceService] Forcing measurement flag reset on connect');
      this.isMeasuring = false;
    }

    const store = useDeviceStore.getState();
    
    const deviceInfo = {
      uid: 'MOCK-DEVICE-001',
      uidBytes: new Uint8Array([0x00, 0x01, 0x02, 0x03, 0x04, 0x05]),
      serialNumber: 'MOCK-SN-12345',
      firmwareVersion: '2.0.0-mock',
      hardwareVersion: '1.0',
    };

    store.setDeviceInfo(deviceInfo);
    store.setConnectionState('connected');
    
    // Set realistic device state for health checks
    store.updateDeviceState({
      busy: false,
      progress: 0,
      electrodeDetected: true,
      electrodeType: 'unknown',
      temperature: 25,
      humidity: 45,
      batteryLevel: 85,
      batteryVoltage: 3.7,
      powerState: 'standby',
      warnings: 0,
    });

    this.isConnected = true;
    logger.info('[MockDeviceService] Connected to mock device');

    return {
      success: true,
      deviceInfo: { ...deviceInfo, serialNumber: 12345 },
    };
  }

  /**
   * Simulate device disconnection
   */
  async disconnect(): Promise<void> {
    await this.delay(100);
    
    const store = useDeviceStore.getState();
    store.reset();
    
    this.isConnected = false;
    this.isMeasuring = false;  // Reset measurement flag on disconnect
    logger.info('[MockDeviceService] Disconnected from mock device');
  }

  /**
   * Simulate LED blink
   */
  async blink(): Promise<DevMessage | null> {
    await this.delay(200);
    logger.debug('[MockDeviceService] Blink triggered');
    return this.createMockDevMessage();
  }

  /**
   * Return current mock device state
   */
  async getState(): Promise<DevMessage | null> {
    await this.delay(50);
    
    // Return full device state for health checks
    logger.debug('[MockDeviceService] getState returning mock state');
    return {
      uid: new Uint8Array([0x00, 0x01, 0x02, 0x03, 0x04, 0x05]),
      serialNumber: 12345,
      version: {
        hardware: 2,
        firmware: 200,  // 2.00
        bluetooth: 0,
        protocol: 0,
      },
      durationMs: 0,
      errorCode: 0,
      state: {
        processingId: 0n,
        progressPct: this.isMeasuring ? 50 : 0,
        updateOffset: 0,
        electrode: ElectrodeDetect.CODE_1,
        temperatureC: 25,
        humidityPct: 45,
        batteryPct: 85,
        batteryV: 3.7,
        powerState: PowerState.STANDBY,
        issueBitmask: 0n,
      },
      limits: {
        pointsMax: 0,
        imageMax: 0,
      },
    };
  }

  /**
   * Simulate custom command
   */
  async sendCustomCommand(
    trigger: Triggers,
    _impedanceExec?: ImpedanceExec
  ): Promise<DevMessage | null> {
    await this.delay(100);
    logger.debug('[MockDeviceService] Custom command:', trigger);
    return this.createMockDevMessage();
  }

  /**
   * Simulate full measurement cycle with progress updates
   */
  async runMeasurement(options: MeasurementOptions): Promise<MeasurementResult> {
    logger.debug('[MockDeviceService] runMeasurement called, isConnected:', this.isConnected, 'isMeasuring:', this.isMeasuring);
    
    if (!this.isConnected) {
      logger.warn('[MockDeviceService] Device not marked as connected, auto-connecting...');
      await this.connect();
    }

    if (this.isMeasuring) {
      logger.error('[MockDeviceService] Attempted to start measurement while already measuring');
      throw new Error('Mock device is busy - measurement already in progress');
    }

    const store = useDeviceStore.getState();
    this.isMeasuring = true;

    logger.info('[MockDeviceService] Starting mock measurement...');

    store.setConnectionState('busy');
    store.updateMeasurementProgress({
      isRunning: true,
      progress: 0,
      startedAt: new Date(),
    });

    const startTime = Date.now();
    const totalDuration = 5000; // 5 seconds
    const progressSteps = 20;

    try {
      // Simulate measurement progress
      for (let i = 0; i <= progressSteps; i++) {
        const progress = (i / progressSteps) * 100;
        
        store.updateMeasurementProgress({ progress });
        options.onProgress?.(progress);
        options.onStateUpdate?.(true, progress);

        logger.debug(`[MockDeviceService] Progress: ${progress}%`);

        if (i < progressSteps) {
          await this.delay(totalDuration / progressSteps);
        }

        // Check for abort
        if (options.abortSignal?.aborted) {
          throw new Error('Measurement cancelled');
        }
      }

      // Generate mock result
      const result: MeasurementResult = {
        deviceUid: 'MOCK-DEVICE-001',
        deviceSerialNumber: 'MOCK-SN-12345',
        firmwareVersion: '1.0.0-mock',
        hardwareVersion: '1.0',
        durationMs: Date.now() - startTime,
        impedanceResult: this.createMockImpedanceResult(),
        errorCode: 0,
        completedAt: new Date(),
      };

      store.setMeasurementResult(result);
      store.setConnectionState('connected');
      store.updateMeasurementProgress({ isRunning: false, progress: 100 });

      logger.info('[MockDeviceService] Measurement complete');
      return result;

    } catch (error) {
      store.setConnectionState('connected');
      store.updateMeasurementProgress({ isRunning: false, progress: 0 });
      throw error;
    } finally {
      this.isMeasuring = false;
    }
  }

  /**
   * Cancel mock measurement
   */
  async cancelMeasurement(): Promise<void> {
    logger.info('[MockDeviceService] Cancelling measurement');
    this.isMeasuring = false;
    
    const store = useDeviceStore.getState();
    store.updateMeasurementProgress({ isRunning: false, progress: 0 });
    store.setConnectionState('connected');
  }

  /**
   * Mock resend — not applicable, returns null
   */
  async resend(): Promise<DevMessage | null> {
    await this.delay(50);
    return this.createMockDevMessage();
  }

  /**
   * Mock tryAutoReconnect — always succeeds if already connected
   */
  async tryAutoReconnect(): Promise<boolean> {
    if (this.isConnected) return true;
    await this.connect();
    return true;
  }

  /**
   * Mock subscribeMeasurement — not applicable in mock; resolves immediately with a mock message
   */
  async subscribeMeasurement(_options: {
    onProgress?: (progress: number) => void;
    onStateUpdate?: (state: import('@/types/device').DeviceState) => void;
    abortSignal?: AbortSignal;
  }): Promise<DevMessage> {
    await this.delay(100);
    return this.createMockDevMessage();
  }

  // ========== Private Helper Methods ==========

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private createMockDevMessage(): DevMessage {
    return {
      uid: new Uint8Array([0x00, 0x01, 0x02, 0x03, 0x04, 0x05]),
      serialNumber: 12345,
      version: {
        hardware: 2,
        firmware: 200,  // 2.00
        bluetooth: 0,
        protocol: 0,
      },
      durationMs: 0,
      errorCode: 0,
      state: {
        processingId: this.isMeasuring ? 1n : 0n,
        progressPct: 0,
        updateOffset: 0,
        electrode: ElectrodeDetect.CODE_1,
        temperatureC: 25,
        humidityPct: 45,
        batteryPct: 85,
        batteryV: 3.7,
        powerState: PowerState.STANDBY,
        issueBitmask: 0n,
      },
      limits: {
        pointsMax: 0,
        imageMax: 0,
      },
    };
  }

  private createMockImpedanceResult(): ImpedanceResult {
    // Generate realistic impedance spectroscopy curve matching device firmware output
    // Based on electrochemical impedance spectroscopy (EIS) patterns.
    // Real device returns: frequencyHz array + one ImpedanceData entry per channel,
    // each holding its own magnitudeOhm/phaseRad/timeMs/currentAcUA arrays. The mock
    // has never simulated multiple distinct channel curves, so it wraps a single
    // CHAN.CH1 entry.

    const frequencyHz = [
      10.0, 12.25, 14.92, 18.13, 22.01, 26.75, 32.6, 39.93, 49.26, 61.37,
      77.41, 99.19, 129.49, 172.7, 235.65, 328.85, 467.94, 675.75, 985.35, 1444.77,
      2124.32, 3127.47, 4606.75, 6787.07, 10000.0
    ];

    // Baseline: relatively flat impedance (~15k-10k Ω range)
    // Specimen: varies based on pathogen response (can increase or decrease)
    const isBaseline = this.measurementPhase === 'baseline';

    const magnitudeOhm: number[] = [];
    const phaseRad: number[] = [];

    // Generate impedance values that follow realistic EIS curve
    frequencyHz.forEach((_freq, idx) => {
      const normalized = idx / (frequencyHz.length - 1); // 0 to 1

      // Baseline: flat, high impedance with small variations
      if (isBaseline) {
        // High impedance at low freq, gradual decrease to ~10k at high freq
        const baseMagnitude = 15000 - normalized * 4600; // 15k → 10.4k
        const noise = (Math.random() - 0.5) * 200; // ±100 Ω
        const magnitude = baseMagnitude + noise;

        // Phase: small angles (capacitive behavior, mostly resistive)
        const basePhase = Math.sin(normalized * Math.PI * 0.8) * 0.15; // Peak ~0.15 rad
        const phaseNoise = (Math.random() - 0.5) * 0.01;
        const phase = basePhase + phaseNoise;

        magnitudeOhm.push(Math.max(magnitude, 10000)); // Clamp to realistic minimum
        phaseRad.push(Math.max(phase, 0.008)); // Clamp to positive small angles
      } else {
        // Specimen: can vary dramatically depending on pathogen/sample
        // Simulate specimen that shows characteristic impedance signature
        const specimenFactor = 0.85 + Math.random() * 0.3; // 0.85-1.15 variation
        const baseMagnitude = (15000 - normalized * 4600) * specimenFactor;
        const noise = (Math.random() - 0.5) * 300;
        const magnitude = baseMagnitude + noise;

        // Phase: more pronounced for specimen, peaks mid-range
        const basePhase = Math.sin(normalized * Math.PI * 1.2) * 0.25; // Peak ~0.25 rad
        const phaseNoise = (Math.random() - 0.5) * 0.015;
        const phase = basePhase + phaseNoise;

        magnitudeOhm.push(Math.max(magnitude, 9000));
        phaseRad.push(Math.max(phase, 0.002));
      }
    });

    // Synthetic time/current arrays alongside the existing magnitude/phase generation —
    // the mock doesn't simulate real acquisition timing or excitation current, so these
    // are simple placeholders matching point order and length.
    const timeMs = frequencyHz.map((_freq, idx) => idx * 10);
    const currentAcUA = frequencyHz.map(() => 100);

    return {
      frequencyHz,
      data: [
        {
          channel: CHAN.CH1,
          magnitudeOhm,
          phaseRad,
          timeMs,
          currentAcUA,
          pointBitmask: frequencyHz.map(() => 0),
        },
      ],
    };
  }
}
